import { ReactElement, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/staff/Button";
import Close from "../../../components/Atomos/Icons/Close";
import Play from "../../../components/Atomos/Icons/Play";
import { TrashCan } from "../../../components/Atomos/Icons/TrashCan";
import Modal from "../../../components/Atomos/Modals/Modal";
import { useDynamicPageMeta } from "../../../hooks/useDynamicPageMeta";
import IIntegratorRunLog, {
  IntegratorFailedStage,
} from "../../../interfaces/models/IIntegratorRunLog";
import { getFirmById } from "../../../Services/Firm";
import { firmShortLabel } from "../../../utils/firmDisplayName";
import {
  deleteIntegratorRunLog,
  getIntegratorRunLogAssets,
  getIntegratorRunLogById,
  setIntegratorRunLogStaffReview,
  startQueuedIntegratorImport,
  type IntegratorRunLogAssets,
} from "../../../Services/IntegratorRunLog";
import { toastError, toastInfo, toastSuccess } from "../../../utils/toast";
import AssetCard, {
  AssetPreviewModal,
  type AssetPreviewTarget,
} from "./AssetCard";
import ImportResultSummaryTables from "./ImportResultSummary";
import ReviewLists from "./ReviewLists";
import {
  fromRunLogSummary,
  toImportResultSummary,
  toReviewLists,
  type ImportResultSummary,
  type ImportReviewLists,
} from "./resultSummaryModel";
import {
  formatStaffReviewDisplay,
  isRunLogClosed,
  STATUS_CLASS,
  STATUS_LABEL,
  triggerLabel,
} from "./runLogDisplay";
import { PANEL, SECTION_TITLE } from "./staffSurface";

const LIST_ROUTE = "/integrator-engine/firms";
const EMPTY = "—";
const ACTION_BUTTON_CLASS = "w-full justify-center sm:w-[12.5rem]";

const FAILED_STAGE_LABEL: Record<IntegratorFailedStage, string> = {
  download: "Download",
  store: "Store",
  map: "Interpret",
  aop: "Practice areas",
  office: "Offices",
  person: "Professionals",
  storeResult: "Store result",
};

function isRunLogForId(
  value: unknown,
  runLogId: string
): value is IIntegratorRunLog {
  return (
    typeof value === "object" &&
    value != null &&
    "id" in value &&
    (value as IIntegratorRunLog).id === runLogId
  );
}

function formatDateTime(ms?: number): string {
  if (!ms) return EMPTY;
  return new Date(ms).toLocaleString("en-US");
}

function formatFileSize(bytes?: number): string {
  if (bytes == null) return EMPTY;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(ms?: number): string {
  if (ms == null) return EMPTY;
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m ${rest}s`;
}

function closedAt(runLog: IIntegratorRunLog): number | undefined {
  return runLog.finishedDate ?? runLog.skippedDate ?? runLog.fetchedDate;
}

function hasStoredFile(assets: IntegratorRunLogAssets | null): boolean {
  if (!assets) return false;
  return Boolean(assets.blobPath || assets.jsonBlobPath || assets.resultBlobPath);
}

export default function ViewIntegratorRunLog(): ReactElement {
  const { runLogId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();

  const stateLog = isRunLogForId(location.state, runLogId ?? "")
    ? location.state
    : undefined;

  const [runLog, setRunLog] = useState<IIntegratorRunLog | undefined>(stateLog);
  const [firmName, setFirmName] = useState("");
  const [assets, setAssets] = useState<IntegratorRunLogAssets | null>(null);
  const [resultSummary, setResultSummary] = useState<ImportResultSummary | null>(
    stateLog?.summary ? fromRunLogSummary(stateLog.summary) : null
  );
  const [reviewLists, setReviewLists] = useState<ImportReviewLists | null>(null);
  const [reviewUnavailable, setReviewUnavailable] = useState(false);
  const [loading, setLoading] = useState(!stateLog);
  const [notFound, setNotFound] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isReviewUpdating, setIsReviewUpdating] = useState(false);
  const [confirmClearReviewOpen, setConfirmClearReviewOpen] = useState(false);

  useEffect(() => {
    setPageMeta({
      title: firmName ? `${firmName} — Run log` : "Run log",
      breadcrumbLabels: runLogId
        ? {
            [`/integrator-engine/run-logs/${runLogId}`.toLowerCase()]:
              firmName || "Run log",
          }
        : {},
    });
    return () => clearPageMeta();
  }, [runLogId, firmName, setPageMeta, clearPageMeta]);

  useEffect(() => {
    if (!runLogId) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      setLoading(!stateLog);
      let loaded: IIntegratorRunLog | undefined;
      try {
        loaded = stateLog ?? (await getIntegratorRunLogById(runLogId));
        if (cancelled) return;
        if (!loaded) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setRunLog(loaded);
        if (loaded.summary) {
          setResultSummary(fromRunLogSummary(loaded.summary));
        }

        const [firm, signed] = await Promise.all([
          getFirmById(loaded.firmId).catch(() => undefined),
          getIntegratorRunLogAssets({ id: loaded.id, firmId: loaded.firmId }),
        ]);
        if (cancelled) {
          return;
        }
        if (firm) {
          setFirmName(firmShortLabel(firm));
        }
        setAssets(signed.assets);
        setLoading(false);

        if (signed.assets.resultBlobPath) {
          try {
            const raw = await loadImportResult(signed.assets.resultBlobPath.url);
            if (cancelled) return;
            setResultSummary(toImportResultSummary(raw));
            setReviewLists(toReviewLists(raw));
          } catch {
            if (!cancelled) setReviewUnavailable(true);
          }
        }
      } catch (error: any) {
        if (cancelled) return;
        if (!loaded) setNotFound(true);
        toastError(error?.message ?? "Could not load this run log.");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [runLogId, stateLog]);

  const isActing = isStarting || isDeleting || isReviewUpdating;

  const applyFinishedRunLog = async (loaded: IIntegratorRunLog) => {
    setRunLog(loaded);
    if (loaded.summary) {
      setResultSummary(fromRunLogSummary(loaded.summary));
    } else {
      setResultSummary(null);
    }
    setReviewLists(null);
    setReviewUnavailable(false);

    try {
      const signed = await getIntegratorRunLogAssets({
        id: loaded.id,
        firmId: loaded.firmId,
      });
      setAssets(signed.assets);
      if (signed.assets.resultBlobPath) {
        try {
          const raw = await loadImportResult(signed.assets.resultBlobPath.url);
          setResultSummary(toImportResultSummary(raw));
          setReviewLists(toReviewLists(raw));
        } catch {
          setReviewUnavailable(true);
        }
      }
    } catch {
      // The run already finished; missing signed URLs should not hide that.
    }
  };

  const handleRunNow = async () => {
    if (!runLog || isActing) return;
    setIsStarting(true);
    try {
      const loaded = (await startQueuedIntegratorImport({
        id: runLog.id,
        firmId: runLog.firmId,
      }))?.runLog;
      if (!loaded) {
        toastError("The run finished without returning a log.");
        return;
      }
      await applyFinishedRunLog(loaded);
      if (loaded.status === "success") {
        toastSuccess("Import completed.");
      } else if (loaded.status === "skipped") {
        toastInfo("Feed unchanged since the last run — nothing new was stored.");
      } else {
        toastError(loaded.errorMessage ?? "The run finished with an error.", {
          autoClose: 6000,
        });
      }
    } catch (error: any) {
      toastError(error?.message ?? "Could not start this run.", {
        autoClose: 6000,
      });
    } finally {
      setIsStarting(false);
    }
  };

  const handleMarkReviewed = async () => {
    if (!runLog || isActing) {
      return;
    }
    setIsReviewUpdating(true);
    try {
      const updated = await setIntegratorRunLogStaffReview({
        id: runLog.id,
        firmId: runLog.firmId,
        reviewed: true,
      });
      setRunLog(updated);
      toastSuccess("Run log marked reviewed.");
    } catch (error: any) {
      toastError(error?.message ?? "Could not update staff review.", {
        autoClose: 6000,
      });
    } finally {
      setIsReviewUpdating(false);
    }
  };

  const handleClearReview = async () => {
    if (!runLog || isActing) {
      return;
    }
    setIsReviewUpdating(true);
    try {
      const updated = await setIntegratorRunLogStaffReview({
        id: runLog.id,
        firmId: runLog.firmId,
        reviewed: false,
      });
      setRunLog(updated);
      setConfirmClearReviewOpen(false);
      toastSuccess("Review cleared.");
    } catch (error: any) {
      toastError(error?.message ?? "Could not update staff review.", {
        autoClose: 6000,
      });
    } finally {
      setIsReviewUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!runLog || isActing) return;
    setIsDeleting(true);
    try {
      await deleteIntegratorRunLog({ id: runLog.id, firmId: runLog.firmId });
      toastSuccess("Run log deleted.");
      navigate(LIST_ROUTE);
    } catch (error: any) {
      toastError(error?.message ?? "Could not delete this run log.", {
        autoClose: 6000,
      });
      setIsDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center px-4 text-gray-600">
        Loading…
      </div>
    );
  }

  if (notFound || !runLog) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-gray-700">This run log was not found.</p>
        <Button
          className="w-full max-w-xs sm:w-auto sm:max-w-none"
          text="Back to Import Module Dashboard"
          color="secondary"
          variant="filled"
          size="fixed"
          onClick={() => navigate(LIST_ROUTE)}
        />
      </div>
    );
  }

  const status = runLog.status;
  const storedRawFeedOnError = status === "error" && Boolean(assets?.blobPath);

  return (
    <>
      <Modal
        isModalOpen={confirmClearReviewOpen}
        toggleModal={() => !isReviewUpdating && setConfirmClearReviewOpen(false)}
        title="Clear staff review"
        titleColor="text-secondary font-semibold text-lg"
        compact
        outerPaddingOnly
        compactBodyScrolls={false}
        className="w-full p-10 !max-w-md !overflow-visible border border-gray-200 shadow-lg sm:!max-w-lg"
        bodyClassName="!pt-0"
      >
        <div className="flex flex-col gap-4">
          <p className="text-left text-sm leading-relaxed text-[#474F56] sm:text-base">
            Clear the staff review on this run log? The log will show as not
            reviewed until someone marks it again.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <Button
              className={ACTION_BUTTON_CLASS}
              text="No, go back"
              onClick={() => !isReviewUpdating && setConfirmClearReviewOpen(false)}
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<Close />}
              disabled={isReviewUpdating}
            />
            <Button
              className={ACTION_BUTTON_CLASS}
              text={isReviewUpdating ? "Saving…" : "Yes, clear"}
              onClick={() => void handleClearReview()}
              color="secondary"
              variant="filled"
              size="fixed"
              disabled={isReviewUpdating}
            />
          </div>
        </div>
      </Modal>

      <Modal
        isModalOpen={confirmDeleteOpen}
        toggleModal={() => !isDeleting && setConfirmDeleteOpen(false)}
        title="Delete run log"
        titleColor="text-secondary font-semibold text-lg"
        compact
        outerPaddingOnly
        compactBodyScrolls={false}
        className="w-full p-10 !max-w-md !overflow-visible border border-gray-200 shadow-lg sm:!max-w-lg"
        bodyClassName="!pt-0"
      >
        <div className="flex flex-col gap-4">
          <p className="text-left text-sm leading-relaxed text-[#474F56] sm:text-base">
            Delete this run log? This cannot be undone. If the log is still
            open, the firm will no longer be blocked and can be imported again.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <Button
              className={ACTION_BUTTON_CLASS}
              text="No, go back"
              onClick={() => !isDeleting && setConfirmDeleteOpen(false)}
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<Close />}
              disabled={isDeleting}
            />
            <Button
              className={ACTION_BUTTON_CLASS}
              text={isDeleting ? "Deleting…" : "Yes, delete"}
              onClick={() => void handleDelete()}
              color="secondary"
              variant="filled"
              size="fixed"
              disabled={isDeleting}
            />
          </div>
        </div>
      </Modal>

      <div className="flex min-w-0 flex-col">
        <div className="flex w-full items-center justify-between rounded-t-lg bg-accent-Default-dark px-5 py-2.5 text-white">
          <p className="font-decimal-medium text-base">
            {firmName || runLog.firmId}
          </p>
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLASS[status]}`}
          >
            {STATUS_LABEL[status]}
          </span>
        </div>

      <div className="flex flex-col gap-5 bg-white p-5">
        <OutcomeHeader
          runLog={runLog}
          onOpenPrevious={() => {
            if (!runLog.previousSuccessLogId) return;
            navigate(`/integrator-engine/run-logs/${runLog.previousSuccessLogId}`);
          }}
        />

        {resultSummary && <ImportResultSummaryTables summary={resultSummary} />}

        {reviewLists && <ReviewLists lists={reviewLists} firmId={runLog.firmId} />}
        {reviewUnavailable && (
          <p className="text-sm text-gray-600">
            The review lists could not be loaded from storage. Download the
            import result to inspect who was added, updated or flagged.
          </p>
        )}

        <StoredFiles
          runLog={runLog}
          assets={assets}
          storedRawFeedOnError={storedRawFeedOnError}
          onOpenPrevious={() => {
            if (!runLog.previousSuccessLogId) return;
            navigate(`/integrator-engine/run-logs/${runLog.previousSuccessLogId}`);
          }}
        />

        <ExecutionDetails runLog={runLog} />

        <div className="flex flex-col gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            {isRunLogClosed(status) && (
              <>
                {runLog.staffReviewedDate ? (
                  <Button
                    className={ACTION_BUTTON_CLASS}
                    text="Clear review"
                    color="neutral"
                    variant="outlined"
                    size="fixed"
                    disabled={isActing}
                    tooltip="Remove the staff review sign-off"
                    onClick={() => setConfirmClearReviewOpen(true)}
                  />
                ) : (
                  <Button
                    className={ACTION_BUTTON_CLASS}
                    text={isReviewUpdating ? "Saving…" : "Mark as reviewed"}
                    color="secondary"
                    variant="filled"
                    size="fixed"
                    disabled={isActing}
                    tooltip="Record that WSG staff reviewed this run log"
                    onClick={() => void handleMarkReviewed()}
                  />
                )}
              </>
            )}
            {status === "queued" && (
              <Button
                className={ACTION_BUTTON_CLASS}
                text={isStarting ? "Running…" : "Run Now"}
                color="secondary"
                variant="filled"
                size="fixed"
                icon={<Play />}
                disabled={isActing}
                tooltip={
                  isStarting
                    ? "This import is running in this request…"
                    : "Run this queued import now, without creating a new log"
                }
                onClick={() => void handleRunNow()}
              />
            )}
            <Button
              className={ACTION_BUTTON_CLASS}
              text="Delete"
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<TrashCan />}
              disabled={isActing}
              tooltip="Remove this run log"
              onClick={() => setConfirmDeleteOpen(true)}
            />
            <Button
              className={ACTION_BUTTON_CLASS}
              text="Manage professionals"
              color="neutral"
              variant="outlined"
              size="fixed"
              disabled={isActing}
              tooltip="Review the firm's professionals by segment"
              onClick={() => navigate(`/integrator-engine/professionals/${runLog.firmId}`)}
            />
          </div>
          <Button
            className={ACTION_BUTTON_CLASS}
            text="Back"
            color="neutral"
            variant="outlined"
            size="fixed"
            icon={<Close />}
            disabled={isActing}
            onClick={() => navigate(LIST_ROUTE)}
          />
        </div>
      </div>
      </div>
    </>
  );
}

function OutcomeHeader({
  runLog,
  onOpenPrevious,
}: {
  runLog: IIntegratorRunLog;
  onOpenPrevious: () => void;
}): ReactElement {
  const status = runLog.status;

  return (
    <section>
      <p className="text-sm text-gray-600">
        {formatDateTime(closedAt(runLog))}
        <span className="mx-2 text-gray-300">·</span>
        {triggerLabel(runLog)}
      </p>

      {status === "error" && runLog.errorMessage && (
        <p className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {runLog.errorMessage}
          {(runLog.httpStatus != null || runLog.sourceUrl) && (
            <span className="mt-1 block text-red-700">
              {[
                runLog.httpStatus != null ? `HTTP ${runLog.httpStatus}` : null,
                runLog.sourceUrl,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          )}
        </p>
      )}

      {status === "skipped" && (
        <p className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          The feed has not changed since the last successful run.
          {runLog.previousSuccessLogId && (
            <>
              {" "}
              <button
                type="button"
                className="font-medium underline underline-offset-2"
                onClick={onOpenPrevious}
              >
                Open that run
              </button>
            </>
          )}
        </p>
      )}

      {status === "queued" && (
        <p className="mt-4 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          This run never reached this firm — it is still queued. Run it now, or
          delete it so a new import can start.
        </p>
      )}

      {status === "in-progress" && (
        <p className="mt-4 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          This firm&apos;s download is still in progress, or the process stopped
          before the log was closed. Delete it if it is stuck, so the firm can
          be imported again.
        </p>
      )}

      {isRunLogClosed(status) && (
        <p className="mt-4 text-sm text-gray-700">
          <span className="font-medium text-gray-800">Staff review:</span>{" "}
          {formatStaffReviewDisplay(runLog) ?? "Not yet reviewed"}
        </p>
      )}
    </section>
  );
}

function StoredFiles({
  runLog,
  assets,
  storedRawFeedOnError,
  onOpenPrevious,
}: {
  runLog: IIntegratorRunLog;
  assets: IntegratorRunLogAssets | null;
  storedRawFeedOnError: boolean;
  onOpenPrevious: () => void;
}): ReactElement {
  const [previewTarget, setPreviewTarget] = useState<AssetPreviewTarget | null>(
    null
  );

  if (runLog.status === "skipped" && !hasStoredFile(assets)) {
    return (
      <section className={PANEL}>
        <h2 className={SECTION_TITLE}>Stored files</h2>
        <p className="text-sm text-gray-600">
          This run did not store files. The feed is the one from the last
          successful run.
          {runLog.previousSuccessLogId && (
            <>
              {" "}
              <button
                type="button"
                className="font-medium text-gray-font underline underline-offset-2"
                onClick={onOpenPrevious}
              >
                Open that run
              </button>
            </>
          )}
        </p>
      </section>
    );
  }

  return (
    <section className={PANEL}>
      <h2 className={SECTION_TITLE}>Stored files</h2>
      {storedRawFeedOnError && (
        <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          This run stored the feed before it failed. Open it to inspect the
          body.
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <AssetCard
          title="Raw feed"
          description="The XML exactly as the feed returned it."
          kind="xml"
          link={assets?.blobPath ?? null}
          extra={assets?.blobPath ? formatFileSize(runLog.fileSize) : undefined}
          emphasize={storedRawFeedOnError}
          onPreview={() => {
            if (!assets?.blobPath) return;
            setPreviewTarget({
              title: "Raw feed",
              kind: "xml",
              link: assets.blobPath,
            });
          }}
        />
        <AssetCard
          title="JSON transcode"
          description="Structural JSON of the same feed. Values stay strings."
          kind="json"
          link={assets?.jsonBlobPath ?? null}
          onPreview={() => {
            if (!assets?.jsonBlobPath) return;
            setPreviewTarget({
              title: "JSON transcode",
              kind: "json",
              link: assets.jsonBlobPath,
            });
          }}
        />
        <AssetCard
          title="Import result"
          description="What the integrators inserted, updated or failed, plus mapping issues."
          kind="json"
          link={assets?.resultBlobPath ?? null}
          onPreview={() => {
            if (!assets?.resultBlobPath) return;
            setPreviewTarget({
              title: "Import result",
              kind: "json",
              link: assets.resultBlobPath,
            });
          }}
        />
      </div>
      <AssetPreviewModal
        target={previewTarget}
        onClose={() => setPreviewTarget(null)}
      />
    </section>
  );
}

function ExecutionDetails({ runLog }: { runLog: IIntegratorRunLog }): ReactElement {
  return (
    <section className={PANEL}>
      <h2 className={SECTION_TITLE}>Execution details</h2>
      <dl className="grid grid-cols-1 gap-x-8 gap-y-4 text-sm md:grid-cols-2">
        <Field label="Source URL" value={runLog.sourceUrl || EMPTY} />
        <Field
          label="HTTP status"
          value={runLog.httpStatus != null ? String(runLog.httpStatus) : EMPTY}
        />
        <Field label="Trigger" value={triggerLabel(runLog)} />
        <Field
          label="Failed stage"
          value={
            runLog.failedStage ? FAILED_STAGE_LABEL[runLog.failedStage] : EMPTY
          }
        />
        <Field label="Queued" value={formatDateTime(runLog.queuedDate)} />
        <Field label="Started" value={formatDateTime(runLog.startedDate)} />
        <Field label="Fetched" value={formatDateTime(runLog.fetchedDate)} />
        <Field label="Finished" value={formatDateTime(runLog.finishedDate)} />
        <Field
          label="Download duration"
          value={formatDuration(runLog.downloadDurationMs)}
        />
        <Field label="File size" value={formatFileSize(runLog.fileSize)} />
        <Field label="Content hash" value={runLog.contentHash || EMPTY} mono />
        <Field label="Run id" value={runLog.runId} mono />
      </dl>
    </section>
  );
}

async function loadImportResult(url: string): Promise<unknown> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Storage answered ${response.status}`);
  }
  return response.json();
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): ReactElement {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd
        className={`mt-1 break-all text-gray-font ${mono ? "font-mono text-xs" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}
